import { FilterProps, useDebouncedSearchParam, useSearchParam, useSearchParams } from '@tlx/astro-shared';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { IntlShape } from 'react-intl';

export interface Filter extends FilterProps {
    labelPlural?: string; //used for "All [departments/employee categories/...]"
    query: string;
    defaultValue?: string[];
    options?: string[];
    className?: string;
    placeholder?: string;
    dontSendAsQueryParam?: boolean; //TRIP-51413 - really long query strings can cause 414 Request-URI Too Long
}
export interface FilterState {
    [filterName: string]: Filter;
}

export const createFilters = <T extends FilterState>(filters: T) => filters;

type CreateFilter = Omit<Filter, 'query'> & {
    query?: (value: string) => string;
};

export function useTableFilter(props: CreateFilter): Filter {
    const searchParam = useSearchParam(props.name, props.defaultValue?.toString());
    return {
        ...props,
        placeholder: props.placeholder ?? props.label + '...',
        query: props.query?.(searchParam) ?? searchParam,
    };
}

export function useDebouncedTableFilter(props: CreateFilter, delay: number): Filter {
    const searchParam = useDebouncedSearchParam(props.name, props.defaultValue?.toString(), delay);
    return {
        ...props,
        placeholder: props.placeholder ?? props.label + '...',
        query: props.query?.(searchParam) ?? searchParam,
    };
}

export function addFiltersToApiParam(apiParams: URLSearchParams, filters?: FilterState, onlyIds?: boolean) {
    //adds for example "departmentIds=123"
    if (filters) {
        Object.values(filters)
            .filter((filter) => filter.query && (onlyIds || !filter.dontSendAsQueryParam) && filter.name !== 'view')
            .forEach((filter) => {
                apiParams.append(filter.name, filter.query);
            });
    }
}

export function useResetFilters(filters: FilterState) {
    const filterNames = Object.values(filters)
        .filter((filter) => !filter.quick || filter.name === 'query')
        .map((filter) => filter.name);
    const searchParams = useSearchParams();
    const navigate = useNavigate();

    return useCallback(() => {
        const nextSearchParams = new URLSearchParams(searchParams);

        for (const name of filterNames) {
            nextSearchParams.delete(name);
        }

        navigate(`?${nextSearchParams}`, { replace: true });
    }, [searchParams, navigate, filterNames]);
}

export function focusOnSearchFilter() {
    document.getElementsByName('query')[0]?.focus();
    const filterOverflowing = document.getElementsByClassName('atl-filter-bar--overflowing').length > 0;
    //opens the filter if the searchFilter overflowed and was put inside it
    if (filterOverflowing) {
        const filterButton = document.getElementsByClassName('atl-filter-popover-button')[0] as HTMLElement;
        filterButton?.click();
    }
}

export function useIsAnyFilterActive(filters: FilterState) {
    const searchParams = useSearchParams();
    return Object.values(filters).some((filter) => searchParams.has(filter.name));
}

export function textAll(filter: Filter, intl: IntlShape) {
    const optionAll = intl.formatMessage({ id: 'option_all' });
    if (filter.labelPlural) {
        return optionAll.replace(')', ` ${filter.labelPlural.toLowerCase()})`);
    }
    return optionAll;
}

export function textMultiple(filter: Filter, count: number) {
    if (filter.labelPlural) {
        return `${count} ${filter.labelPlural.toLowerCase()}`;
    }
    return String(count);
}

export function textPlaceholder(filter: Filter, intl: IntlShape) {
    const searchFor = intl.formatMessage({ id: 'text_search_for' });
    if (filter.labelPlural) {
        return `${searchFor} ${filter.labelPlural.toLowerCase()}`;
    }
    return undefined;
}

export function addPeriodToUrl(period: Filter) {
    const periodAsString = period.query ? period.query : (period.defaultValue?.[0] ?? ''); //the period in this case is something like '2024-01-01,2024-03-31,Month'
    return `period=${periodAsString.substring(0, 7)}`; //in that case it will return '2024-01'
}
